import React, { useState, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { API_BASE_URL } from '@/utils/api';

// Configure PDF.js worker - using unpkg for reliable version matching
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Map original URLs to clean gallery PDF IDs
const PDF_ID_MAP = {
  'hto61qm2_Example%201%20sketch.pdf': 'example-1-sketch',
  'ot5fw0k5_Example%201%20Completed.pdf': 'example-1-completed',
  'a077anxi_Example%202%20Completed.pdf': 'example-2-completed',
  'iifmqtro_Example%203%20leasehold%20plan%20completed.pdf': 'example-3-completed',
  'rr4ftk7v_Example%205%20Licence%20plan%20completed.pdf': 'example-5-completed',
};

const getCleanPdfUrl = (url) => {
  // Check if this is a known gallery PDF
  for (const [filename, pdfId] of Object.entries(PDF_ID_MAP)) {
    if (url.includes(filename)) {
      return `${API_BASE_URL}/api/gallery-pdf/${pdfId}`;
    }
  }
  // Fallback to proxy for unknown PDFs
  if (url.includes('customer-assets.emergentagent.com')) {
    return `${API_BASE_URL}/api/proxy-pdf?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const PDFViewer = ({ url, title, rotation = 0 }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Use clean gallery URL or proxy URL for external PDFs
  const proxyUrl = useMemo(() => getCleanPdfUrl(url), [url]);

  // Function to open PDF in new tab by fetching as blob (bypasses ad-blockers)
  const handleOpenPdf = async () => {
    setDownloading(true);
    try {
      const response = await fetch(proxyUrl);
      if (!response.ok) throw new Error('Failed to fetch PDF');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('Error opening PDF:', err);
      alert('Unable to open PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF');
    setLoading(false);
  };

  if (error) {
    return (
      <div className="bg-white rounded shadow-lg p-8 text-center">
        <p className="text-gray-600 mb-4">{title}</p>
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={handleOpenPdf}
          disabled={downloading}
          className="text-blue-600 hover:underline font-medium disabled:opacity-50"
        >
          {downloading ? 'Downloading...' : 'Download PDF →'}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded shadow-lg">
      {loading && (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading PDF...</p>
        </div>
      )}
      <Document
        file={proxyUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={null}
        className="flex justify-center"
      >
        <Page 
          pageNumber={pageNumber} 
          renderTextLayer={false}
          renderAnnotationLayer={false}
          width={550}
          rotate={rotation}
          className="rounded"
        />
      </Document>
      
      {!loading && numPages && (
        <div className="p-4 border-t text-center space-y-2">
          {numPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                disabled={pageNumber <= 1}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {pageNumber} of {numPages}
              </span>
              <button
                onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                disabled={pageNumber >= numPages}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300"
              >
                Next
              </button>
            </div>
          )}
          <button 
            onClick={handleOpenPdf}
            disabled={downloading}
            className="block w-full text-blue-600 hover:underline font-medium text-sm disabled:opacity-50"
          >
            {downloading ? 'Opening...' : 'Open Full PDF →'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
